import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Send, Users, Megaphone, BarChart2, MessageSquare, ArrowRight, Zap, CheckCircle, Circle, Crown } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/campaigns/stats/overview'),
      api.get('/campaigns'),
      api.get('/emails/plan-status'),
    ]).then(([s, c, p]) => {
      setStats(s.data.stats);
      setCampaigns(c.data.campaigns?.slice(0, 5) || []);
      setPlanStatus(p.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding:32, display:'flex', alignItems:'center', gap:12, color:'var(--muted)' }}>
      <div className="spinner" /> Loading...
    </div>
  );

  const statCards = [
    { label:'Campaigns', value: stats?.total_campaigns ?? 0, icon: Megaphone, color:'#7c3aed', bg:'rgba(124,58,237,.12)' },
    { label:'Total Leads', value: stats?.total_leads ?? 0, icon: Users, color:'#3b82f6', bg:'rgba(59,130,246,.12)' },
    { label:'Emails Sent', value: stats?.emails_sent ?? 0, icon: Send, color:'#10b981', bg:'rgba(16,185,129,.12)' },
    { label:'Open Rate', value: `${stats?.open_rate ?? 0}%`, icon: BarChart2, color:'#f59e0b', bg:'rgba(245,158,11,.12)' },
    { label:'Reply Rate', value: `${stats?.reply_rate ?? 0}%`, icon: MessageSquare, color:'#ec4899', bg:'rgba(236,72,153,.12)' },
  ];

  const hasAnyCampaign = (stats?.total_campaigns ?? 0) > 0;
  const hasAnyLead = (stats?.total_leads ?? 0) > 0;
  const hasAnySent = (stats?.emails_sent ?? 0) > 0;

  const checklist = [
    { label: 'Create your first campaign', done: hasAnyCampaign, to: '/campaigns' },
    { label: 'Import leads via CSV', done: hasAnyLead, to: '/leads' },
    { label: 'Send your first AI email', done: hasAnySent, to: '/campaigns' },
    { label: 'Set up auto follow-ups', done: hasAnyCampaign, to: '/campaigns' },
  ];

  const allDone = checklist.every(c => c.done);
  const isFreePlan = planStatus?.is_free_plan;
  const emailsUsed = planStatus?.total_emails_sent || 0;
  const freeLimit = planStatus?.free_limit || 20;
  const usagePercent = Math.min(100, Math.round((emailsUsed / freeLimit) * 100));
  const limitReached = planStatus?.limit_reached;

  return (
    <div style={{ padding:32, maxWidth:1100 }}>

      {/* FREE PLAN UPGRADE BANNER */}
      {isFreePlan && (
        <div style={{
          background: limitReached ? 'rgba(220,38,38,.06)' : usagePercent >= 70 ? 'rgba(217,119,6,.06)' : 'rgba(108,71,255,.06)',
          border: `1px solid ${limitReached ? 'rgba(220,38,38,.2)' : usagePercent >= 70 ? 'rgba(217,119,6,.2)' : 'rgba(108,71,255,.15)'}`,
          borderRadius:10, padding:'12px 16px',
          marginBottom:20, display:'flex',
          alignItems:'center', justifyContent:'space-between', gap:12
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Crown size={16} color={limitReached ? 'var(--red)' : usagePercent >= 70 ? 'var(--amber)' : 'var(--accent)'} />
            <div>
              <p style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                {limitReached
                  ? 'Free plan limit reached — upgrade to keep sending'
                  : `Free plan — ${freeLimit - emailsUsed} emails remaining`
                }
              </p>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                <div style={{ width:120, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                  <div style={{
                    width:`${usagePercent}%`, height:'100%', borderRadius:2,
                    background: limitReached ? 'var(--red)' : usagePercent >= 70 ? 'var(--amber)' : 'var(--accent)',
                    transition:'width .3s'
                  }}/>
                </div>
                <span style={{ fontSize:11, color:'var(--muted)' }}>{emailsUsed}/{freeLimit} used</span>
              </div>
            </div>
          </div>
          <Link to="/pricing">
            <button style={{
              background:'var(--accent)', color:'#fff', border:'none',
              padding:'7px 14px', borderRadius:7, fontSize:13,
              fontWeight:600, cursor:'pointer', fontFamily:'inherit',
              whiteSpace:'nowrap'
            }}>
              Upgrade →
            </button>
          </Link>
        </div>
      )}

      {/* HEADER */}
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:22, fontWeight:800, letterSpacing:'-.02em' }}>
          Good to see you, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p style={{ color:'var(--muted)', marginTop:4, fontSize:14 }}>
          Here's your outreach overview.
        </p>
      </div>

      {/* ONBOARDING CHECKLIST */}
      {!allDone && (
        <div style={{
          background:'var(--card)', border:'1px solid rgba(108,71,255,.3)',
          borderRadius:12, padding:'18px 20px', marginBottom:24,
          position:'relative', overflow:'hidden'
        }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, var(--accent), transparent)' }}/>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <p style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>Get started with ColdCraft AI</p>
              <p style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>
                {checklist.filter(c => c.done).length} of {checklist.length} steps complete
              </p>
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--accent2)', background:'var(--glow)', padding:'4px 10px', borderRadius:6 }}>
              {Math.round((checklist.filter(c => c.done).length / checklist.length) * 100)}%
            </div>
          </div>
          <div style={{ height:4, background:'var(--border)', borderRadius:2, marginBottom:16, overflow:'hidden' }}>
            <div style={{
              height:'100%',
              width:`${(checklist.filter(c => c.done).length / checklist.length) * 100}%`,
              background:'linear-gradient(90deg, var(--accent), var(--accent2))',
              borderRadius:2, transition:'width .4s ease'
            }}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {checklist.map(({ label, done, to }) => (
              <Link key={label} to={done ? '#' : to} style={{
                display:'flex', alignItems:'center', gap:9, padding:'9px 12px', borderRadius:8,
                background: done ? 'rgba(0,214,143,.05)' : 'var(--bg2)',
                border:`1px solid ${done ? 'rgba(0,214,143,.15)' : 'var(--border)'}`,
                textDecoration:'none', transition:'all .15s',
                cursor: done ? 'default' : 'pointer'
              }}>
                {done ? <CheckCircle size={15} color="var(--green)" /> : <Circle size={15} color="var(--muted)" />}
                <span style={{
                  fontSize:12.5, fontWeight:500,
                  color: done ? 'var(--muted)' : 'var(--text)',
                  textDecoration: done ? 'line-through' : 'none'
                }}>{label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* STAT CARDS */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:24 }}>
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="stat-card" style={{ padding:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <p style={{ fontSize:10.5, color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:10 }}>{label}</p>
                <p style={{ fontSize:30, fontWeight:800, letterSpacing:'-.03em', lineHeight:1, color:'var(--text)' }}>{value}</p>
              </div>
              <div style={{ width:40, height:40, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Icon size={18} color={color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* BOTTOM GRID */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h2 style={{ fontSize:14, fontWeight:700 }}>Recent Campaigns</h2>
            <Link to="/campaigns" style={{ fontSize:12, color:'var(--accent2)', textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}>
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {campaigns.length === 0 ? (
            <div className="empty">
              <div style={{ width:44, height:44, borderRadius:12, background:'var(--glow)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                <Megaphone size={20} color="var(--accent2)" />
              </div>
              <p style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>No campaigns yet</p>
              <p style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>Create your first campaign to start sending AI-personalized emails</p>
              <Link to="/campaigns" className="btn btn-primary btn-sm">Create campaign</Link>
            </div>
          ) : campaigns.map(c => (
            <Link key={c.id} to={`/campaigns/${c.id}`} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderBottom:'1px solid var(--border)', textDecoration:'none', transition:'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.025)'}
              onMouseLeave={e => e.currentTarget.style.background=''}
            >
              <div>
                <p style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{c.name}</p>
                <p style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{c.emails_sent || 0} sent{c.replies_received > 0 && ` · ${c.replies_received} replies`}</p>
              </div>
              <span className={`badge ${c.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{c.status}</span>
            </Link>
          ))}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card">
            <h2 style={{ fontSize:14, fontWeight:700, marginBottom:14 }}>Quick Actions</h2>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {[
                { to:'/campaigns', icon:Megaphone, label:'New campaign', desc:'Set up a new outreach campaign' },
                { to:'/leads', icon:Users, label:'Import leads', desc:'Upload a CSV of contacts' },
                { to:'/campaigns', icon:Zap, label:'Generate AI emails', desc:'AI-write personalized emails' },
              ].map(({ to, icon: Icon, label, desc }) => (
                <Link key={label} to={to} style={{ display:'flex', alignItems:'center', gap:12, padding:11, borderRadius:9, border:'1px solid var(--border)', textDecoration:'none', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(108,71,255,.4)'; e.currentTarget.style.background='var(--glow)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background=''; }}
                >
                  <div style={{ width:32, height:32, borderRadius:8, background:'var(--glow)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon size={14} color="var(--accent2)" />
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{label}</p>
                    <p style={{ fontSize:11, color:'var(--muted)' }}>{desc}</p>
                  </div>
                  <ArrowRight size={12} color="var(--muted)" />
                </Link>
              ))}
            </div>
          </div>

          <div style={{ background:'linear-gradient(135deg, rgba(108,71,255,.15), rgba(139,109,255,.08))', border:'1px solid rgba(108,71,255,.25)', borderRadius:12, padding:'16px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <div style={{ width:28, height:28, borderRadius:7, background:'var(--glow)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Zap size={13} color="var(--accent2)" fill="var(--accent2)" />
              </div>
              <p style={{ fontSize:13, fontWeight:700, color:'var(--accent2)' }}>AI Tip</p>
            </div>
            <p style={{ fontSize:12.5, color:'var(--muted)', lineHeight:1.6 }}>
              {hasAnySent
                ? 'Follow-up emails get 2-3x more replies than first emails. Make sure follow-ups are enabled on your active campaigns.'
                : 'Start with a small lead list of 5-10 highly targeted contacts. Quality beats quantity every time in cold outreach.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}